import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';

import CardWrapper from 'components/Common/CardWrapper';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import Header from 'components/Common/Header';
import { hasAtLeastRole } from 'api/v1Helpers';
import { selectEffectiveStaffRole, selectSelectedParishId } from 'redux/selectors/parishContext';
import { AppDispatch, RootState } from 'redux/store';
import { fetchParishStripeStatus, startStripeOnboarding } from 'redux/slices/payments/stripeOnboardingSlice';

/**
 * Phase 11 — Stripe Connect onboarding/status only.
 * Status: CLERK+. Onboarding link: MERCHANT_ADMIN+.
 * Live charges/onboarded flags come from DB (account.updated webhook); refresh after return.
 */
export default function StripeOnboarding() {
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const parishName = useSelector((s: RootState) => s.user.selectedParishName);
    const actorRole = useSelector(selectEffectiveStaffRole);
    const canStartOnboarding = hasAtLeastRole(actorRole, 'MERCHANT_ADMIN');

    const { status, loading, linkLoading, error, inflightParishId } = useSelector((s: RootState) => s.stripeOnboarding);

    const loadStatus = useCallback(() => {
        if (!parishId) return;
        dispatch(fetchParishStripeStatus({ parishId }));
    }, [dispatch, parishId]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Refresh when the tab becomes visible again (e.g. return from Stripe).
    // visibilitychange only — pairing with `focus` double-fires and duplicates GETs.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') loadStatus();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [loadStatus]);

    const displayStatus = parishId && inflightParishId === parishId ? status : null;

    const handleCompleteSetup = async () => {
        if (!parishId || !canStartOnboarding) return;
        const res = await dispatch(startStripeOnboarding({ parishId }));
        // payload is the success shape on fulfillment, or the rejected thunk's error-message
        // string on failure (see createAsyncThunkWrapper.ts) — payload?.url is falsy for either.
        const payload = (res as { payload?: { url?: string } | string | null })?.payload;
        if (payload && typeof payload === 'object' && payload.url) {
            window.location.assign(payload.url);
        }
    };

    const connectedLabel = displayStatus?.connected
        ? displayStatus.chargesEnabled
            ? 'Ready to accept payments'
            : 'Connected — finish setup'
        : 'Not connected';

    const chipColor = displayStatus?.chargesEnabled ? 'success' : displayStatus?.connected ? 'warning' : 'default';

    return (
        <Box sx={{ backgroundColor: '#F2F2F6', flex: 1, minHeight: '100%' }}>
            <Box sx={{ p: 2.75 }}>
                <Header />
            </Box>
            <Box sx={{ px: 2.75, pb: 3 }}>
                <CardWrapper>
                    <MuiLoader absolute loading={loading || linkLoading} />
                    <Typography sx={{ fontSize: { xs: 18, sm: 24 }, mb: 1, color: '#220E40', fontWeight: 500 }} variant="h1">
                        Stripe Setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Connect this parish to Stripe so it can receive payouts. Status updates after Stripe finishes onboarding
                        (webhook-driven).
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {!parishId && <Alert severity="warning">Select a parish before managing Stripe.</Alert>}

                    {error && parishId && !loading && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {parishId && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight={500}>
                                {parishName || 'Selected parish'}
                            </Typography>

                            {displayStatus && (
                                <>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip label={connectedLabel} color={chipColor} />
                                        <Chip
                                            label={displayStatus.connected ? 'Account linked' : 'No Stripe account'}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            label={displayStatus.chargesEnabled ? 'Charges enabled' : 'Charges not enabled'}
                                            variant="outlined"
                                            size="small"
                                            color={displayStatus.chargesEnabled ? 'success' : 'default'}
                                        />
                                    </Stack>

                                    {displayStatus.onboardedAt && (
                                        <Typography variant="body2" color="text.secondary">
                                            Onboarded at: {new Date(displayStatus.onboardedAt).toLocaleString()}
                                        </Typography>
                                    )}
                                </>
                            )}

                            {!displayStatus && !loading && !error && (
                                <Typography variant="body2" color="text.secondary">
                                    No status loaded yet.
                                </Typography>
                            )}

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                {canStartOnboarding ? (
                                    <Button
                                        title={displayStatus?.chargesEnabled ? 'Update Stripe Account' : 'Complete Stripe Setup'}
                                        onClick={handleCompleteSetup}
                                        loading={linkLoading}
                                        disabled={!parishId || linkLoading}
                                        sx={{ width: { xs: '100%', sm: 260 } }}
                                    />
                                ) : (
                                    <Alert severity="info" sx={{ flex: 1 }}>
                                        Viewing status only. Merchant Admin or higher is required to start Stripe onboarding.
                                    </Alert>
                                )}
                                <Button
                                    title="Refresh status"
                                    onClick={loadStatus}
                                    disabled={!parishId || loading}
                                    sx={{ width: { xs: '100%', sm: 180 } }}
                                />
                            </Stack>
                        </Stack>
                    )}
                </CardWrapper>
            </Box>
        </Box>
    );
}
